import React, {
    type ChangeEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Label as RoccoLabel } from '@rocco/components/label';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { FilterConfigModalItemSearchable } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { SearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/searchable-list-filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { FilterGroupOptions } from '@rocco/types/filter-group-options';
import { useDebounceFn } from 'ahooks';
import { Input as RoccoInput } from '@rocco/components/input';
import { isSearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';

export const FiltersModalSearchableList = (
    props: FilterModalElementProps<
        FilterConfigModalItemSearchable,
        SearchableListFilterState
    > & {
        onAlphabeticalGrouping: (args: AggregationArgs) => void;
        /** `sidebar` = compact 5.0 directory browse styling. */
        variant?: 'default' | 'sidebar';
        /** Flip colours for the dark (projects) browse theme. */
        isDark?: boolean;
        /**
         * Suppress the selected-item chips above the list. For hosts that
         * already surface selections elsewhere (the refine sheet's chips
         * row) — the checked rows in the list are representation enough.
         */
        hideSelectedChips?: boolean;
    },
) => {
    const {
        filterConfigItem,
        filterState,
        onFilterStateUpdate,
        onAlphabeticalGrouping,
        displayMode,
        variant = 'default',
        isDark = false,
        hideSelectedChips = false,
    } = props;
    const isSidebar = variant === 'sidebar';

    const { searchFilters } = useFiltersContext();
    const { handleAggregate } = searchFilters;

    const defaultShowCount = 60;
    const [showCount, setShowCount] = useState(defaultShowCount);
    const [isLoading, setIsLoading] = useState(false);

    const filterKey = filterConfigItem.filterKey;

    // Aggregate callbacks close over filter state from the render that issued
    // the request. Keep a ref so onSuccess merges into what the user has typed
    // since then — writing a stale `search` into a focused input dismisses the
    // iOS keyboard.
    const filterStateRef = useRef(filterState);
    filterStateRef.current = filterState;
    const lastSearchQueryRef = useRef<string | null>(null);
    const searchGenerationRef = useRef(0);

    /** Gets updated state from aggregation, saving properties chosen by user **/
    const getUpdatedState = (state: SearchableListFilterState) => {
        const items = state.items.map((item) => {
            const selectedItem = state.selectedItems.find(
                (selectedItem) => selectedItem.value === item.value,
            );

            return selectedItem ? { ...selectedItem } : { ...item };
        });

        return { ...state, items };
    };

    const { run: searchAggregateRun } = useDebounceFn(
        (searchQuery: string) => {
            const current = filterStateRef.current;
            // Same query already in flight or just applied — do not queue
            // another search. Re-issuing with the previous page cursor
            // walks pages forever and flashes the count + option list.
            if (lastSearchQueryRef.current === searchQuery) {
                return;
            }
            lastSearchQueryRef.current = searchQuery;
            searchGenerationRef.current += 1;
            const generation = searchGenerationRef.current;
            let succeeded = false;

            handleAggregate({
                aggregateType: 'searchListSearch',
                aggregateFilter: filterKey,
                aggregationsParams: {
                    [filterConfigItem.aggregationProperty]: {
                        search: searchQuery,
                        after: undefined,
                        size: current.size,
                    },
                },
                onStart: () => {
                    if (generation === searchGenerationRef.current) {
                        setIsLoading(true);
                    }
                },
                onFinish: () => {
                    if (generation !== searchGenerationRef.current) return;

                    setIsLoading(false);
                    if (!succeeded) {
                        lastSearchQueryRef.current = null;
                    }
                },
                onSuccess: (data) => {
                    if (generation !== searchGenerationRef.current) return;

                    const newListState = data.states?.[filterKey];

                    if (
                        !newListState ||
                        !isSearchableListFilterState(newListState)
                    ) {
                        return;
                    }

                    succeeded = true;
                    const newItems = newListState.items ?? [];
                    const latest = filterStateRef.current;

                    onFilterStateUpdate(
                        getUpdatedState({
                            ...latest,
                            search: searchQuery,
                            items: newItems,
                            after: newListState.after ?? null,
                            hasMoreItems:
                                newListState.hasMoreItems ??
                                newListState.items.length >= latest.size,
                            groupedBy: undefined,
                            isSearching: false,
                        }),
                        false,
                    );
                },
            });
        },
        { wait: 500 },
    );

    const searchGroupAlphabetically = useCallback(
        (groupBy: keyof FilterGroupOptions | undefined) => {
            // Grouping changes the list independently of the current search.
            // Invalidate an older search response and allow that query to be
            // submitted again when the user returns to free-text search.
            searchGenerationRef.current += 1;
            lastSearchQueryRef.current = null;

            onAlphabeticalGrouping({
                aggregateType: groupBy
                    ? 'groupAlphabetical'
                    : 'searchListLoadMore',
                aggregateFilter: filterKey,
                aggregationsParams: {
                    [filterConfigItem.aggregationProperty]: {
                        after: undefined,
                        size: 1000,
                        startsWith: groupBy,
                    },
                },
                onStart: () => setIsLoading(true),
                onFinish: () => setIsLoading(false),
                onSuccess: (data) => {
                    const newListState = data.states?.[filterKey];

                    if (
                        newListState &&
                        isSearchableListFilterState(newListState)
                    ) {
                        setShowCount(defaultShowCount);
                        onFilterStateUpdate(
                            getUpdatedState({
                                ...filterState,
                                search: '',
                                items: newListState?.items ?? [],
                                groupedBy: groupBy,
                            }),
                            false,
                        );
                    }
                },
            });
        },
        [
            filterConfigItem.aggregationProperty,
            filterKey,
            filterState,
            onAlphabeticalGrouping,
            onFilterStateUpdate,
        ],
    );

    // One row per value. Two options can arrive for the same brand when the
    // source data differs only by case — "BOFFI" and "Boffi", "Cotto" and
    // "cotto" — and they share a value, so they shared a DOM id (invalid, and
    // it breaks label association), ticking both produced `?brands=cotto,cotto`
    // against a single pill, and the funnel badge read 1 for two ticked boxes.
    const uniqueItems = useMemo(() => {
        const seen = new Set<string>();
        return filterState.items.filter((item) => {
            const key = String(item.value);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [filterState.items]);

    const showMoreCount = uniqueItems.length - showCount;
    const showItems = uniqueItems.slice(0, showCount);

    const columns =
        displayMode === DisplayMode.Desktop &&
        filterConfigItem.columns &&
        filterConfigItem.columns > 0
            ? filterConfigItem.columns
            : 1;

    const containerStyles = {
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${Math.ceil(showItems.length / columns)}, auto)`,
    };

    return (
        <div className={isSidebar ? undefined : 'pt-2'}>
            <SearchableList
                isLoading={isLoading}
                variant={variant}
                isDark={isDark}
                searchInputPlaceholder={filterConfigItem.searchInputPlaceholder}
                searchQuery={filterState.search}
                groupAlphabetically={
                    // The compact sidebar hides the A–Z "jump to letter" row.
                    isSidebar
                        ? false
                        : (filterConfigItem.allowAlphabeticalGrouping ?? false)
                }
                groupedBy={filterState.groupedBy}
                firstCharacterGroups={filterState.firstCharacterGroups}
                onGroupingChange={searchGroupAlphabetically}
                onSearchQueryChange={(updatedSearchQuery) => {
                    // Draft lives in SearchableList. Defer parent updates to
                    // the debounced aggregate so each keystroke does not
                    // re-render the sheet under the focused field.
                    searchAggregateRun(updatedSearchQuery);
                }}
            >
                {/* Selected-item chips at the top. Hidden in the sidebar and
                    wherever the host surfaces selections itself — they
                    already show as checked rows in the list below. */}
                {!isSidebar &&
                    !hideSelectedChips &&
                    filterState.selectedItems.length > 0 && (
                        <div className="mb-10 flex flex-wrap gap-3">
                            {filterState.selectedItems.map((item, index) => (
                                <FiltersModalSearchableListItem
                                    key={index}
                                    item={item}
                                    filterState={filterState}
                                    onFilterStateUpdate={onFilterStateUpdate}
                                    isSelected={true}
                                    variant={variant}
                                    isDark={isDark}
                                />
                            ))}
                        </div>
                    )}

                {showItems.length === 0 && !isLoading && filterState.search ? (
                    // A search that matches nothing used to render zero rows
                    // and nothing else, so the group collapsed to a blank
                    // between its own input and the next group's heading and
                    // read as a rendering failure rather than an answer.
                    <p
                        className={cn(
                            'm-0 py-2 text-sm',
                            isDark ? 'text-white/60' : 'text-[#767676]',
                        )}
                    >
                        No {(filterConfigItem.label ?? 'options').toLowerCase()}{' '}
                        match “{filterState.search}”
                    </p>
                ) : (
                    <div
                        className={cn(
                            isSidebar
                                ? 'flex flex-col gap-1.5'
                                : 'grid grid-flow-col-dense gap-2',
                        )}
                        style={isSidebar ? undefined : containerStyles}
                    >
                        {showItems.map((item, index) => (
                            <FiltersModalSearchableListItem
                                key={index}
                                item={item}
                                filterState={filterState}
                                onFilterStateUpdate={onFilterStateUpdate}
                                variant={variant}
                                isDark={isDark}
                            />
                        ))}
                    </div>
                )}

                {showMoreCount > 0 && (
                    <div
                        className={cn(
                            'flex cursor-pointer flex-col items-center',
                            isSidebar ? 'mt-3' : 'mt-10',
                        )}
                        onClick={() => {
                            setShowCount(showCount + defaultShowCount);
                        }}
                    >
                        <span
                            className={cn(
                                'font-medium',
                                isSidebar
                                    ? cn(
                                          'text-sm',
                                          isDark
                                              ? 'text-white/80'
                                              : 'text-[#343434]',
                                      )
                                    : 'text-gray-900',
                            )}
                        >
                            Display{' '}
                            {showMoreCount < defaultShowCount
                                ? showMoreCount
                                : defaultShowCount}{' '}
                            more
                        </span>
                        <ChevronDown
                            className={isSidebar ? 'size-4' : 'size-4.5'}
                        />
                    </div>
                )}
            </SearchableList>
        </div>
    );
};

interface FiltersModalSearchableListItemProps {
    item: SingleValueFilterState;
    filterState: SearchableListFilterState;
    isSelected?: boolean;
    variant?: 'default' | 'sidebar';
    isDark?: boolean;
    onFilterStateUpdate: (
        state: SearchableListFilterState,
        shouldAggregate?: boolean,
    ) => void;
}

const FiltersModalSearchableListItem = (
    props: FiltersModalSearchableListItemProps,
) => {
    const {
        item,
        filterState,
        onFilterStateUpdate,
        isSelected = false,
        variant = 'default',
        isDark = false,
    } = props;
    const {
        searchFilters: { handleToggleSearchableList },
    } = useFiltersContext();

    const id = `${item.value}${isSelected ? '-selected' : ''}`;
    const isSidebar = variant === 'sidebar';

    const toggle = (checked: boolean | 'indeterminate') => {
        if (handleToggleSearchableList) {
            onFilterStateUpdate(
                handleToggleSearchableList(filterState, item, !!checked),
            );
        }
    };

    // Sidebar: left-aligned checkbox matching the rest of the 5.0 filter panel
    // (15px #343434 box, 14px label). Selected chips keep their pill outline.
    if (isSidebar && !isSelected) {
        return (
            <label className="flex cursor-pointer items-start gap-2">
                <RoccoCheckbox
                    id={id}
                    checked={item.checked}
                    onCheckedChange={toggle}
                    className={cn(
                        'mt-0.5 size-[0.9375rem] shrink-0 rounded-[0.125rem] border',
                        isDark
                            ? 'border-white/60 data-[state=checked]:border-white'
                            : 'border-[#343434]',
                    )}
                />
                <span
                    className={cn(
                        'text-[0.875rem] leading-[1.358]',
                        isDark ? 'text-white/80' : 'text-[#343434]',
                    )}
                >
                    {item.label}
                </span>
            </label>
        );
    }

    return (
        <div
            className={cn(
                'flex items-center',
                isSelected
                    ? 'rounded-2 border px-3 py-2'
                    : 'flex-row-reverse justify-between',
                isSelected && isDark
                    ? 'border-white/40 text-white'
                    : isSelected
                      ? 'border-gray-900'
                      : undefined,
            )}
        >
            <RoccoCheckbox
                id={id}
                checked={item.checked}
                onCheckedChange={toggle}
            />
            <RoccoLabel
                htmlFor={id}
                target={isSelected ? 'checkbox' : 'checkboxReversed'}
                singleLine
                className={isSidebar && isDark ? 'text-white' : undefined}
            >
                {item.label}
            </RoccoLabel>
        </div>
    );
};

interface SearchableListProps extends React.PropsWithChildren {
    isLoading: boolean;
    groupAlphabetically: boolean;
    searchInputPlaceholder: string;
    searchQuery?: string;
    onSearchQueryChange?: (searchQuery: string) => void;
    groupedBy?: keyof FilterGroupOptions;
    onGroupingChange?: (groupBy?: keyof FilterGroupOptions) => void;
    firstCharacterGroups?: Partial<Record<keyof FilterGroupOptions, number>>;
    variant?: 'default' | 'sidebar';
    isDark?: boolean;
}

const SearchableList = (props: SearchableListProps) => {
    const {
        children,
        isLoading,
        searchInputPlaceholder,
        searchQuery = '',
        onSearchQueryChange,
        groupedBy,
        onGroupingChange,
        groupAlphabetically,
        firstCharacterGroups,
        variant = 'default',
        isDark = false,
    } = props;
    const isSidebar = variant === 'sidebar';

    // Draft value while the field is focused so parent re-renders (option
    // list updates, sheet keyboard offset) cannot rewrite the controlled
    // input mid-keystroke — that dismisses the iOS keyboard.
    const [draftSearch, setDraftSearch] = useState(searchQuery);
    const isFocusedRef = useRef(false);
    const onSearchQueryChangeRef = useRef(onSearchQueryChange);
    onSearchQueryChangeRef.current = onSearchQueryChange;

    useEffect(() => {
        if (!isFocusedRef.current) {
            setDraftSearch(searchQuery);
        }
    }, [searchQuery]);

    const handleFocus = useCallback(() => {
        isFocusedRef.current = true;
    }, []);

    const handleBlur = useCallback(() => {
        isFocusedRef.current = false;
    }, []);

    const handleChange = useCallback((next: string) => {
        setDraftSearch(next);
        onSearchQueryChangeRef.current?.(next);
    }, []);

    return (
        <div>
            <div className="flex flex-col items-center justify-center gap-4 md:justify-between">
                <div className="w-full grow">
                    <FilterSearchField
                        isSidebar={isSidebar}
                        isDark={isDark}
                        placeholder={searchInputPlaceholder}
                        value={draftSearch}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={handleChange}
                    />
                </div>

                {groupAlphabetically && onGroupingChange && (
                    <div className="scrollbar-hidden w-[85dvw] overflow-x-scroll md:w-unset md:max-w-[320px]">
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                className={cn(
                                    'min-w-4 text-center text-gray-500 decoration-gray-900 decoration-2 underline-offset-4',
                                    !groupedBy
                                        ? 'text-gray-900 underline'
                                        : undefined,
                                )}
                                onClick={() => onGroupingChange()}
                            >
                                All
                            </button>

                            {Object.entries(FilterGroupOptions).map(
                                ([key, label]) => {
                                    const k = key as keyof FilterGroupOptions;

                                    return (firstCharacterGroups?.[k] ?? 0) >
                                        0 ? (
                                        <button
                                            key={k}
                                            className={cn(
                                                'text-center text-gray-500 decoration-gray-900 decoration-2 underline-offset-4',
                                                groupedBy && groupedBy === k
                                                    ? 'text-gray-900 underline'
                                                    : undefined,
                                                k === 'numeric'
                                                    ? 'min-w-6'
                                                    : 'min-w-3',
                                            )}
                                            onClick={() => onGroupingChange(k)}
                                        >
                                            {label}
                                        </button>
                                    ) : (
                                        <div
                                            key={k}
                                            className={cn(
                                                'min-w-3 cursor-not-allowed text-center text-gray-500/30',
                                                k === 'numeric'
                                                    ? 'min-w-6'
                                                    : 'min-w-3',
                                            )}
                                        >
                                            {label}
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Dim options only — opacity on the focused input can make iOS
                drop the software keyboard while search results load. */}
            <div
                className={cn(
                    isSidebar ? 'mt-3' : 'mt-6',
                    isLoading && 'opacity-50',
                )}
            >
                {children}
            </div>
        </div>
    );
};

/**
 * Isolated so option-list / loading updates do not reconcile the focused
 * field. Trailing chrome is stable — swapping the search icon for a clear
 * button on the first character remounts siblings and iOS drops the keyboard.
 *
 * So the clear control lives INSIDE the postfix rather than replacing it:
 * `showClearButton` is what swaps the postfix for a button of Rocco's own, and
 * that is the swap the line above describes. Holding the postfix and changing
 * only what it draws leaves the field's siblings alone.
 */
const FilterSearchField = memo(function FilterSearchField({
    isSidebar,
    isDark,
    placeholder,
    value,
    onFocus,
    onBlur,
    onChange,
}: {
    isSidebar: boolean;
    isDark: boolean;
    placeholder: string;
    value: string;
    onFocus: () => void;
    onBlur: () => void;
    onChange: (value: string) => void;
}) {
    return (
        <RoccoInput
            type="text"
            inputMode="search"
            enterKeyHint="search"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            color={isSidebar ? 'gray' : 'outline'}
            text="default"
            shape={isSidebar ? 'default' : 'rounded'}
            className={cn(
                // Scales with the page like the type around it. On a touch
                // screen it never drops under 16px: iOS zooms into a focused
                // field set smaller than that. Marked important so the
                // Input's own breakpoint size cannot win over the floor.
                'text-[1rem] md:text-[1rem] pointer-coarse:text-[max(16px,1rem)]!',
                isSidebar &&
                    cn(
                        'h-[2.625rem] rounded-[0.25rem] px-[0.5625rem] placeholder:text-[#717171]',
                        isDark
                            ? 'bg-white/10 text-white placeholder:text-white/40'
                            : 'bg-[#f4f4f4]',
                    ),
            )}
            placeholder={placeholder}
            value={value}
            showClearButton={false}
            postfix={
                value ? (
                    <button
                        type="button"
                        aria-label="Clear"
                        // Keeps the field focused: the pointer-down would blur
                        // it before the click lands.
                        tabIndex={-1}
                        onPointerDown={(event) => event.preventDefault()}
                        onClick={() => onChange('')}
                        className={cn(
                            'pointer-events-auto cursor-pointer border-0 bg-transparent p-0',
                            isDark && 'text-white/70',
                        )}
                    >
                        <X className="size-4.5" />
                    </button>
                ) : (
                    <Search
                        className={cn('size-4.5', isDark && 'text-white/70')}
                    />
                )
            }
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                onChange(evt.target.value);
            }}
        />
    );
});
